import React, { useContext } from "react";
import { AuthFunctionsContext } from "State/AuthFunctionsContext";
import SbInput from "uiCore/SbInput/SbInput";
import SbButton from "uiCore/SbButton/SbButton";
import { sanitizeHtml } from "Utils/sanitizeHtml";
import {
  FormStyled,
  FieldStyled,
  FieldRowStyled,
  LabelStyled,
  LinksRowStyled,
  LinkStyled,
  MessageStyled,
} from "Pages/SbAuthPage/LoginV2.styles";

// Renders only the signup form(s) — the layout is provided by LoginPageLayout.
const SbSignupComponent = () => {
  const {
    username,
    setUsername,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    otp,
    setOtp,
    error,
    sb_auth_handleBlur,
    loadingAuth,
    sb_auth_handleSignupStep1,
    sb_auth_handleSignupStep2,
    sb_auth_handleSignupStep3,
    toggleSignup,
    signupStep,
    companyName,
    setCompanyName,
    jobTitle,
    setJobTitle,
    sb_auth_reSendOtpSignup,
    otpValid
  } = useContext(AuthFunctionsContext);


  const handleNextStep = async () => {
    if (!firstName || !lastName || !username || !companyName || !jobTitle) return;
    await sb_auth_handleSignupStep1();
  };
  const handleVerifyOtp = async () => {
    if (!otp) return;
    sb_auth_handleSignupStep2();
  };
  const handleSignup = async () => {
    if (!password || !confirmPassword) return;
    sb_auth_handleSignupStep3();
  };
  const handleResendOtpClick = (e) => {
    e.preventDefault();
    sb_auth_reSendOtpSignup();
  };

  if (signupStep === 1) {
    return (
      <FormStyled onFinish={handleNextStep}>
        <FieldRowStyled>
          <FieldStyled>
            <LabelStyled htmlFor="firstName">First Name</LabelStyled>
            <SbInput
              id="firstName"
              value={firstName}
              onBlur={sb_auth_handleBlur}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            {error.firstName && <MessageStyled>{error.firstName}</MessageStyled>}
          </FieldStyled>
          <FieldStyled>
            <LabelStyled htmlFor="lastName">Last Name</LabelStyled>
            <SbInput
              id="lastName"
              value={lastName}
              onBlur={sb_auth_handleBlur}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
            {error.lastName && <MessageStyled>{error.lastName}</MessageStyled>}
          </FieldStyled>
        </FieldRowStyled>

        <FieldRowStyled>
          <FieldStyled>
            <LabelStyled htmlFor="companyName">Company Name</LabelStyled>
            <SbInput
              id="companyName"
              value={companyName}
              onBlur={sb_auth_handleBlur}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
            {error.companyName && <MessageStyled>{error.companyName}</MessageStyled>}
          </FieldStyled>
          <FieldStyled>
            <LabelStyled htmlFor="jobTitle">Job Title</LabelStyled>
            <SbInput
              id="jobTitle"
              value={jobTitle}
              onBlur={sb_auth_handleBlur}
              onChange={(e) => setJobTitle(e.target.value)}
              required
            />
            {error.jobTitle && <MessageStyled>{error.jobTitle}</MessageStyled>}
          </FieldStyled>
        </FieldRowStyled>

        <FieldStyled>
          <LabelStyled htmlFor="email">Business Email</LabelStyled>
          <SbInput
            id="email"
            type="email"
            value={username}
            onBlur={sb_auth_handleBlur}
            onChange={(e) => setUsername(e.target.value.trim())}
            required
          />
          {error.username && <MessageStyled>{error.username}</MessageStyled>}
        </FieldStyled>

        {error.authfail && (
          <MessageStyled dangerouslySetInnerHTML={{ __html: sanitizeHtml(error.authfail, { allowNewTabLinks: true }) }} />
        )}

        <SbButton buttonType="accent" htmlType="submit" loading={loadingAuth}>
          Send OTP
        </SbButton>

        <LinksRowStyled>
          <span>
            Already have an account?{" "}
            <LinkStyled onClick={toggleSignup}>Sign In!</LinkStyled>
          </span>
        </LinksRowStyled>
      </FormStyled>
    );
  }

  if (signupStep === 2) {
    return (
      <FormStyled onFinish={handleVerifyOtp}>
        <MessageStyled $success>
          {otpValid ||
            "You'll receive an OTP at your email address. Check your inbox if it doesn't arrive."}
        </MessageStyled>

        <FieldStyled>
          <LabelStyled htmlFor="otp">OTP</LabelStyled>
          <SbInput
            id="otp"
            type="tel"
            inputMode="numeric"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            onBlur={sb_auth_handleBlur}
            maxLength={6}
            required
          />
          {error.otp && <MessageStyled>{error.otp}</MessageStyled>}
        </FieldStyled>

        {error.authfail && (
          <MessageStyled dangerouslySetInnerHTML={{ __html: sanitizeHtml(error.authfail, { allowNewTabLinks: true }) }} />
        )}

        <SbButton buttonType="accent" htmlType="submit" loading={loadingAuth}>
          Verify OTP
        </SbButton>

        <LinksRowStyled>
          <LinkStyled onClick={handleResendOtpClick}>Resend OTP</LinkStyled>
          <LinkStyled onClick={toggleSignup}>Sign In!</LinkStyled>
        </LinksRowStyled>
      </FormStyled>
    );
  }

  return (
    <FormStyled onFinish={handleSignup}>
      <FieldStyled>
        <LabelStyled htmlFor="password">Password</LabelStyled>
        <SbInput
          id="password"
          type="password"
          value={password}
          onBlur={sb_auth_handleBlur}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error.password && <MessageStyled>{error.password}</MessageStyled>}
      </FieldStyled>

      <FieldStyled>
        <LabelStyled htmlFor="confirmPassword">Confirm Password</LabelStyled>
        <SbInput
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onBlur={sb_auth_handleBlur}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        {error.confirmPassword && <MessageStyled>{error.confirmPassword}</MessageStyled>}
      </FieldStyled>

      {error.authfail && (
        <MessageStyled dangerouslySetInnerHTML={{ __html: sanitizeHtml(error.authfail, { allowNewTabLinks: true }) }} />
      )}

      <SbButton buttonType="accent" htmlType="submit" loading={loadingAuth}>
        Sign Up
      </SbButton>

      <LinksRowStyled>
        <span>
          Already have an account?{" "}
          <LinkStyled onClick={toggleSignup}>Sign In!</LinkStyled>
        </span>
      </LinksRowStyled>
    </FormStyled>
  );
};

export default SbSignupComponent;
